import React from 'react';
let gradientSeed = 0;
type IsotypeProps = {
  className?: string;
  /** Renders the mark inside the circular badge (ideal para avatares) */
  badge?: boolean;
  /** Versión de una sola tinta: todo el símbolo usa currentColor */
  monochrome?: boolean;
};

/**
 * Isotipo Creativo Digital: cápsula C + D entrelazadas con las barras de
 * crecimiento. La "C" usa currentColor, por lo que hereda el color del
 * contenedor (tinta sobre fondos claros, blanco sobre fondos oscuros).
 */
export function CDIsotype({
  className = '',
  badge = false,
  monochrome = false
}: IsotypeProps) {
  const id = React.useMemo(() => `cd-${++gradientSeed}`, []);
  const gradient = `url(#${id}-grad)`;
  const mark = <>
      <g mask={`url(#${id}-ring)`} data-id="element-22" data-mp="17jvc2a8j3vpo">
        <rect x="0" y="0" width="64" height="64" fill="currentColor" data-id="element-23" data-mp="17itt7jl9u4xe" />
        <path d="M37 -2 L70 -2 L70 66 L27 66 Z" fill={monochrome ? 'currentColor' : gradient} data-id="element-24" data-mp="24ziyxodcdrc2" />
      </g>
      <g fill={monochrome ? 'currentColor' : gradient} data-id="element-25" data-mp="205o903758g4j">
        <rect x="29.5" y="33.5" width="3" height="6.5" rx="1.5" data-id="element-26" data-mp="13w8neuxw3jeh" />
        <rect x="35" y="30" width="3" height="10" rx="1.5" data-id="element-27" data-mp="18uaeo35gyp11" />
        <rect x="40.5" y="26.5" width="3" height="13.5" rx="1.5" data-id="element-28" data-mp="18th74zyorvhu" />
      </g>
    </>;
  return <svg viewBox="0 0 64 64" className={className} role="presentation" aria-hidden="true" data-id="element-29" data-mp="u4uqmuxboxp1">
      <defs data-id="element-30" data-mp="z24fa8cw15bt">
        <linearGradient id={`${id}-grad`} x1="0" y1="1" x2="1" y2="0" data-id="element-31" data-mp="l4plysjikdek">
          <stop offset="0%" stopColor="#2563FF" data-id="element-32" data-mp="l3ekizdjjhqm" />
          <stop offset="100%" stopColor="#8B5CF6" data-id="element-33" data-mp="p6jjiryex1ai" />
        </linearGradient>
        <mask id={`${id}-ring`} data-id="element-34" data-mp="b6i9alw9chid">
          <rect x="4" y="13" width="56" height="38" rx="19" fill="#fff" data-id="element-35" data-mp="b7jvnmiq4qfe" />
          <rect x="17.5" y="24" width="29" height="16" rx="8" fill="#000" data-id="element-36" data-mp="g5cxk3czuy9v" />
        </mask>
      </defs>
      {badge ? <>
          <circle cx="32" cy="32" r="32" fill="#F5F6FB" data-id="element-37" data-mp="25lukvw38dmlp" />
          <g transform="translate(32 32) scale(0.86) translate(-32 -32)" data-id="element-38" data-mp="2ajdxkrka5rbj">
            {mark}
          </g>
        </> : mark}
    </svg>;
}
type WordmarkProps = {
  className?: string;
  showTagline?: boolean;
  size?: 'sm' | 'md' | 'lg';
  tone?: 'ink' | 'inverse';
};

/**
 * Wordmark oficial: CREATIVO en tinta (o blanco) y DIGITAL en el degradado
 * azul → violeta, con el descriptor opcional.
 */
export function BrandWordmark({
  className = '',
  showTagline = false,
  size = 'sm',
  tone = 'ink'
}: WordmarkProps) {
  const scale = {
    sm: {
      main: 'text-[13px] tracking-[0.30em]',
      sub: 'text-[8.5px] tracking-[0.44em]',
      tag: 'text-[6px] tracking-[0.24em]'
    },
    md: {
      main: 'text-[17px] tracking-[0.32em]',
      sub: 'text-[11px] tracking-[0.46em]',
      tag: 'text-[7.5px] tracking-[0.26em]'
    },
    lg: {
      main: 'text-[22px] tracking-[0.34em]',
      sub: 'text-[14px] tracking-[0.48em]',
      tag: 'text-[9px] tracking-[0.28em]'
    }
  }[size];
  return <span className={`flex flex-col leading-none ${className}`} data-id="element-39" data-mp="35rohcdl6jb8i">
      <span className={`font-semibold ${tone === 'inverse' ? 'text-white' : 'text-ink'} ${scale.main}`} data-id="element-40" data-mp="3e1na9econzb6">
        CREATIVO
      </span>
      <span className={`brand-gradient-text mt-1 font-medium ${scale.sub}`} data-id="element-41" data-mp="3i51s8ak6mpr9">
        DIGITAL
      </span>
      {showTagline && <span className={`mt-1.5 font-light ${tone === 'inverse' ? 'text-white/60' : 'text-ink-muted'} ${scale.tag}`} data-id="element-42" data-mp="3s0v8o510v3eb">
          RECURSOS DIGITALES
        </span>}
    </span>;
}